import { useRef, useEffect, useLayoutEffect, useCallback, useImperativeHandle, forwardRef, useState } from 'react'
import {
  computeBounds, layerNameColor, entitiesInBox, buildSpatialIndex,
  findEntityAtPoint, CLASS_COLORS, rotateEntity,
  closestPointOnSegment, segmentIntersectionPoint, polygonCentroid, perpFoot,
  normLayer, inFocusedClass, inShiftSelection, layerVisibilityKey, isEntityVisible, isEntityInSolo, isEntitySelectable,
  editableShape, liveVertexEntity, hitPolylineGrip, nearestSegmentPoint, insertVertexAtSegment,
  removeVertex, toggleSegmentArc, vertexMenuActions,
} from './utils'
import { segmentCount, segmentEnds, bulgeArray, segMidpoint, bulgeFromCursor } from './bulge.js'
import {
  getEntityBounds, getVisibleWorldBounds, boundsOutsideView, worldToScreen, entityScreenSize,
  getStableScreenAnchor, worldToScreenStable, getEntitiesScreenBounds, getWheelZoomFactor, applyZoomAtScreenPoint,
  shouldRenderEntityInWheelFrame, getEntityScreenTransform, isViewportPanning,
  MAX_VERTEX_HANDLES_PER_FRAME, MIN_VERTEX_HANDLE_SPACING_PX, MIN_ENTITY_SCREEN_SIZE_FOR_HANDLES_PX,
} from './canvasViewport'
import { useResizeObserver } from './shared/lib/hooks'
import { markPerf } from './shared/lib/perf-trace'
import {
  commitSelectionPreviewUpdate, shouldUpdateSelectionPreview, isSelectionIndexCacheValid, buildSelectedOverlayList,
  SELECTED_OVERLAY_LOD_MIN_SCREEN_PX, shouldSkipSelectedOverlayEntity, shouldUseSelectedOverlayLod,
} from './features/canvas-selection'
import { isSceneCacheValid, ghostScreenOffset, ghostBitmapRect } from './features/canvas-scene-cache'

const RULER = 0 // ruler removed; kept as zero so scale-bar math stays consistent
const MAX_SELECTED_GRIP_HOVER_SCAN = 200

// ── Helpers ───────────────────────────────────────────────────────────────────
function screenToWorld(sx, sy, { scale, offsetX, offsetY }) {
  return [(sx - offsetX) / scale, (offsetY - sy) / scale]
}

// Reuses the entity's cached local Path2D instead of rebuilding beginPath/moveTo/
// lineTo every call. Keeping path coordinates close to the entity origin avoids
// canvas precision loss on huge DXF world coordinates while zooming.
function drawEntity(ctx, e, t, anchor, dx = 0, dy = 0) {
  const screenTransform = getEntityScreenTransform(e, t, anchor, dx, dy)
  if (!screenTransform) return
  ctx.setTransform(...screenTransform.matrix)
  ctx.stroke(screenTransform.path)
  ctx.setTransform(1, 0, 0, 1, 0, 0)
}

// ── Ortho constraint (Shift) ──────────────────────────────────────────────────
// Given an anchor point, snap wx/wy to the nearest 90° direction.
function orthoConstrain(anchor, wx, wy) {
  const dx = wx - anchor[0], dy = wy - anchor[1]
  return Math.abs(dx) >= Math.abs(dy)
    ? [anchor[0] + dx, anchor[1]]
    : [anchor[0], anchor[1] + dy]
}

// ── Shape builder helpers ────────────────────────────────────────────────────
function buildArc(p1, pmid, p3, layer) {
  const ax=p1[0],ay=p1[1], bx=pmid[0],by=pmid[1], cx=p3[0],cy=p3[1]
  const D = 2*(ax*(by-cy)+bx*(cy-ay)+cx*(ay-by))
  if (Math.abs(D) < 1e-10) return { type:'LWPOLYLINE', points:[p1,pmid,p3], closed:false, layer }
  const a2=ax*ax+ay*ay, b2=bx*bx+by*by, c2=cx*cx+cy*cy
  const ux=(a2*(by-cy)+b2*(cy-ay)+c2*(ay-by))/D
  const uy=(a2*(cx-bx)+b2*(ax-cx)+c2*(bx-ax))/D
  const r=Math.hypot(ax-ux,ay-uy)
  function norm(a){ return ((a%(2*Math.PI))+2*Math.PI)%(2*Math.PI) }
  const na1=norm(Math.atan2(ay-uy,ax-ux))
  const nam=norm(Math.atan2(by-uy,bx-ux))
  const na3=norm(Math.atan2(cy-uy,cx-ux))
  // Check if nam lies in the CCW arc from na1 to na3
  let spanCCW = norm(na3-na1)
  let midCCW  = norm(nam-na1)
  const ccw   = midCCW < spanCCW
  const N=48, points=[]
  const span = ccw ? spanCCW : norm(na1-na3)
  const dir  = ccw ? 1 : -1
  for (let i=0;i<=N;i++) {
    const angle = na1 + dir*(span*i/N)
    points.push([ux+r*Math.cos(angle), uy+r*Math.sin(angle)])
  }
  return { type:'LWPOLYLINE', points, closed:false, layer }
}

function buildShapeEntity(shape, pts, layer) {
  if (shape === 'rectangle') {
    const [x0,y0]=pts[0],[x1,y1]=pts[1]
    return { type:'LWPOLYLINE', points:[[x0,y0],[x1,y0],[x1,y1],[x0,y1]], closed:true, layer }
  }
  if (shape === 'circle') {
    const radius=Math.hypot(pts[1][0]-pts[0][0],pts[1][1]-pts[0][1])
    if (radius < 1e-10) return null
    return { type:'CIRCLE', center:[...pts[0]], radius, layer }
  }
  if (shape === 'arc') return buildArc(pts[0], pts[1], pts[2], layer)
  return null
}

// Returns LWPOLYLINE points for a circle approximation (for previewing)
function circlePreviewPts(center, radiusPt, N=48) {
  const r = Math.hypot(radiusPt[0]-center[0], radiusPt[1]-center[1])
  const pts = []
  for (let i=0;i<=N;i++) pts.push([center[0]+r*Math.cos(2*Math.PI*i/N), center[1]+r*Math.sin(2*Math.PI*i/N)])
  return pts
}

// ── Snap helper ───────────────────────────────────────────────────────────────
// Priority weights — lower wins over a closer raw distance, but only within
// a narrow margin (max ~1.35x): a type can't win over a candidate of another
// type that's meaningfully closer, only one that's "slightly nearer".
const SNAP_W = { close:0.85, endpoint:0.88, intersection:0.92, center:0.95,
                 midpoint:0.99, quadrant:1.03, geometric_center:1.07,
                 perpendicular:1.11, nearest:1.15 }
const SNAP_COLOR = {
  close:            '#2563eb',
  endpoint:         '#16a34a',
  midpoint:         '#16a34a',
  center:           '#16a34a',
  quadrant:         '#0891b2',
  geometric_center: '#0d9488',
  intersection:     '#d97706',
  perpendicular:    '#7c3aed',
  nearest:          '#64748b',
}
const SNAP_LABEL = {
  close: 'Close', endpoint: 'End', midpoint: 'Mid', center: 'Cen',
  quadrant: 'Quad', geometric_center: 'GCen',
  intersection: 'Int', perpendicular: 'Perp', nearest: 'Near',
}

function findSnapPoint(manifest, polyPts, wx, wy, scale, vl, sl, av, snapPx = 14) {
  const tol = snapPx / scale
  let best = null, bestScore = Infinity, bestType = null

  function check(p, type) {
    if (!p) return
    const d = Math.hypot(wx - p[0], wy - p[1])
    if (d > tol) return
    const score = d * (SNAP_W[type] ?? 1.0)
    if (score < bestScore) { bestScore = score; best = [p[0], p[1]]; bestType = type }
  }

  // ── In-progress polyline ───────────────────────────────────────────────────
  if (polyPts?.length >= 2) {
    check(polyPts[0], 'close')
    for (let i = 1; i < polyPts.length; i++) check(polyPts[i], 'endpoint')
    for (let i = 0; i + 1 < polyPts.length; i++)
      check([(polyPts[i][0]+polyPts[i+1][0])/2,(polyPts[i][1]+polyPts[i+1][1])/2], 'midpoint')
  }

  // Anchor for perpendicular snaps = last placed drawing point
  const anchor = polyPts?.length ? polyPts[polyPts.length - 1] : null

  // Collect segments for intersection pass
  const segs = []

  for (const e of manifest ?? []) {
    if (!isEntitySelectable(e, vl, sl, av)) continue
    if (e.type === 'LINE' && e.start && e.end) {
      const [a, b] = [e.start, e.end]
      check(a, 'endpoint'); check(b, 'endpoint')
      check([(a[0]+b[0])/2, (a[1]+b[1])/2], 'midpoint')
      check(closestPointOnSegment(wx, wy, a[0], a[1], b[0], b[1]), 'nearest')
      if (anchor) check(perpFoot(anchor[0], anchor[1], a[0], a[1], b[0], b[1]), 'perpendicular')
      segs.push([a, b])

    } else if (e.type === 'CIRCLE' && e.center) {
      const [cx, cy, r] = [e.center[0], e.center[1], e.radius]
      check(e.center, 'center')
      // Quadrant points (0° 90° 180° 270°)
      check([cx+r, cy], 'quadrant'); check([cx, cy+r], 'quadrant')
      check([cx-r, cy], 'quadrant'); check([cx, cy-r], 'quadrant')
      // Nearest point on circumference
      const ang = Math.atan2(wy - cy, wx - cx)
      check([cx + r*Math.cos(ang), cy + r*Math.sin(ang)], 'nearest')

    } else if (e.type === 'LWPOLYLINE' && e.points?.length) {
      const pts = e.points
      for (const p of pts) check(p, 'endpoint')
      const n = pts.length
      for (let i = 0; i + 1 < n; i++) {
        const [a, b] = [pts[i], pts[i+1]]
        check([(a[0]+b[0])/2, (a[1]+b[1])/2], 'midpoint')
        check(closestPointOnSegment(wx, wy, a[0], a[1], b[0], b[1]), 'nearest')
        if (anchor) check(perpFoot(anchor[0], anchor[1], a[0], a[1], b[0], b[1]), 'perpendicular')
        segs.push([a, b])
      }
      if (e.closed && n > 2) {
        const [a, b] = [pts[n-1], pts[0]]
        check([(a[0]+b[0])/2, (a[1]+b[1])/2], 'midpoint')
        check(closestPointOnSegment(wx, wy, a[0], a[1], b[0], b[1]), 'nearest')
        if (anchor) check(perpFoot(anchor[0], anchor[1], a[0], a[1], b[0], b[1]), 'perpendicular')
        segs.push([a, b])
        check(polygonCentroid(pts), 'geometric_center')
      }
    }
  }

  // ── Intersection pass — only segments actually within tol of the cursor ────
  const nearby = segs.filter(([a, b]) => {
    if (!(
      Math.min(a[0],b[0]) - tol <= wx && wx <= Math.max(a[0],b[0]) + tol &&
      Math.min(a[1],b[1]) - tol <= wy && wy <= Math.max(a[1],b[1]) + tol
    )) return false
    const cp = closestPointOnSegment(wx, wy, a[0], a[1], b[0], b[1])
    return Math.hypot(wx - cp[0], wy - cp[1]) <= tol
  })
  // Avoid O(n^2) work on large drawings / zoomed-out views
  if (nearby.length <= 200) {
    for (let i = 0; i < nearby.length; i++)
      for (let j = i + 1; j < nearby.length; j++)
        check(segmentIntersectionPoint(nearby[i][0], nearby[i][1], nearby[j][0], nearby[j][1]), 'intersection')
  }

  return best ? { point: best, type: bestType } : null
}

// ── Canvas component ──────────────────────────────────────────────────────────
// The view transform that fits world bounds b into the canvas with `pad` px around it (y up).
function boundsTransform(canvas, b, pad) {
  const dw = Math.max(b.maxX-b.minX, 1e-6), dh = Math.max(b.maxY-b.minY, 1e-6)
  const scale = Math.min((canvas.width-pad*2)/dw,(canvas.height-pad*2)/dh)*0.95
  const cx=(b.minX+b.maxX)/2, cy=(b.minY+b.maxY)/2
  return { scale, offsetX:canvas.width/2-cx*scale, offsetY:canvas.height/2+cy*scale }
}

const DxfCanvas = forwardRef(function DxfCanvas(props, ref) {
  const { manifest, fitKey, pdfBg, visibleLayers, soloLayers, activeView } = props

  const canvasEl     = useRef(null)
  const transformRef = useRef({ scale: 1, offsetX: 0, offsetY: 0 })
  const fenceRef        = useRef([])
  const fencePreviewRef = useRef(new Set())
  const fencePreviewQueryRef = useRef(null)
  const fenceDragRef    = useRef(null)  // { sx, sy, moved } — tracks click-drag-release on the anchored box
  const fenceSelectableRef = useRef([])  // selectable entities, cached at box-anchor time so mousemove doesn't refilter the whole manifest
  const fenceSubtractRef = useRef(false)  // true when shift held — subtract mode
  const spatialIndexRef  = useRef(null)   // spatial index built at fence-anchor time
  const polyRef         = useRef([])
  const cursorRef    = useRef(null)
  const panRef       = useRef(null)
  const interactRef  = useRef(null)
  const shiftRef     = useRef(false)
  const scaleRef      = useRef(null)  // first point of scale measure
  const snapRef       = useRef(null)  // current snap result {point, type}
  const vertexEditRef   = useRef(null)  // { mode, handle, vertexIdx/segIdx, points, bulge, ... } while dragging
  const gripHoverRef    = useRef(null)  // { handle, mode:'vertex'|'midpoint'|'arc', idx } — hovered grip
  const floodHoverRef   = useRef(null)  // { x, y } nearest point on flood segment under cursor (add-vertex hint)
  const [vertexMenu, setVertexMenu] = useState(null) // { handle, vi, x, y, entity } — right-click context menu
  const shapeRef        = useRef([])    // in-progress shape clicks [worldPt, ...]
  const lastClickRef = useRef({ time: 0, x: 0, y: 0 })
  const pickCaptureRef = useRef(null) // { sx, sy } while pick-point mode waiting for mouseup
  const propsRef     = useRef(props)
  propsRef.current   = props
  const rafRef        = useRef(0)     // pending requestAnimationFrame id for coalesced redraws
  const wheelActiveRef = useRef(false)
  const wheelIdleTimerRef = useRef(0)
  const previewRetryTimerRef = useRef(0)
  const manifestIndexRef = useRef({ m: null, byHandle: new Map() }) // handle -> entity, cached per manifest identity
  const selectionIndexCacheRef = useRef(null)
  const selectedOverlayRef = useRef(null) // { key, list } — selected entities that pass the viewport-independent filters
  const sceneCacheRef = useRef(null) // { key, canvas } — static scene bitmap while a point-pick tool is active
  const ghostCacheRef = useRef(null) // { key, canvas, rect } — Move/Copy ghost bitmap at the base point

  // O(1) handle -> entity lookup for the current manifest — avoids re-scanning
  // the whole manifest per selected handle (was the main cause of the canvas
  // freezing when many entities are selected on a large drawing).
  function manifestByHandle(m) {
    const idx = manifestIndexRef.current
    if (idx.m !== m) {
      const byHandle = new Map()
      if (m) for (const e of m) byHandle.set(e.handle, e)
      manifestIndexRef.current = { m, byHandle }
      return byHandle
    }
    return idx.byHandle
  }

  // Strokes the Move/Copy ghost once into a bitmap anchored at the base point; a cursor
  // move then only shifts it. Returns null when it is too large to cache.
  function getMoveGhost(entities, canvas, t, anchor) {
    const key = [entities, canvas.width, canvas.height, t.scale, t.offsetX, t.offsetY]
    if (isSceneCacheValid(ghostCacheRef.current, key)) return ghostCacheRef.current
    const rect = ghostBitmapRect(getEntitiesScreenBounds(entities, t, anchor), canvas.width, canvas.height)
    if (!rect) return (ghostCacheRef.current = null)
    const buf = document.createElement('canvas')
    buf.width = rect.w; buf.height = rect.h
    const gctx = buf.getContext('2d')
    const local = { ...anchor, screenX: anchor.screenX - rect.x, screenY: anchor.screenY - rect.y }
    gctx.strokeStyle = 'rgba(37,99,235,0.65)'; gctx.lineWidth = 1.5 / t.scale
    gctx.setLineDash([4 / t.scale, 3 / t.scale])
    for (const e of entities) drawEntity(gctx, e, t, local)
    return (ghostCacheRef.current = { key, canvas: buf, rect })
  }

  function clearFencePreview() {
    fencePreviewRef.current = new Set()
    fencePreviewQueryRef.current = null
    clearTimeout(previewRetryTimerRef.current)
    previewRetryTimerRef.current = 0
  }

  // ── draw ──────────────────────────────────────────────────────────────────
  const draw = useCallback(() => {
    const canvas = canvasEl.current
    if (!canvas) return
    const finishDrawTrace = markPerf('canvas.draw')
    const {
      manifest: m, visibleLayers: vl, activeView: av,
      classColors: cc, selectedHandles: sh, selectedColorMap: scm, focusHandles: fh,
      isolatedLayer: iso, activeTool: tool, scaleFactor: sf, soloLayers: sl, shiftSelectedLayers: ssl,
    } = propsRef.current
    const t   = transformRef.current
    const ctx = canvas.getContext('2d')
    const isClassified = av === 'classified'
    const ia  = interactRef.current
    const isWheelZooming = wheelActiveRef.current
    const isSelectingBox = fenceRef.current.length === 1 || ia?.mode === 'box'

    // Recompute the selection-box live preview at most once per redraw (see
    // onMouseMove) rather than on every raw mousemove event.
    if (fenceRef.current.length === 1 && cursorRef.current) {
      const [ax, ay] = fenceRef.current[0]
      const [cx, cy] = cursorRef.current
      const mode = cx >= ax ? 'window' : 'crossing'
      const [csx, csy] = worldToScreen(cx, cy, t)
      const last = fencePreviewQueryRef.current
      const now = performance.now()
      const previewDecision = shouldUpdateSelectionPreview(last, { sx: csx, sy: csy, mode, now })
      const src = spatialIndexRef.current ?? fenceSelectableRef.current
      if (previewDecision.update) {
        const finishPreviewTrace = markPerf('selection.preview', {
          mode,
          reason: previewDecision.reason,
          intervalMs: last?.intervalMs ?? 0,
        })
        fencePreviewRef.current = entitiesInBox(src, ax, ay, cx, cy, mode)
        const duration = finishPreviewTrace({ count: fencePreviewRef.current.size })
        fencePreviewQueryRef.current = commitSelectionPreviewUpdate(
          last,
          { sx: csx, sy: csy, mode, now },
          duration,
        )
      } else if (previewDecision.reason === 'throttled') {
        markPerf('selection.preview.skip', {
          reason: previewDecision.reason,
          intervalMs: last?.intervalMs ?? 0,
        })()
        // A throttled preview has no later event to redraw it — without this the
        // highlight stays at an older rectangle once the pointer stops.
        if (!previewRetryTimerRef.current) {
          previewRetryTimerRef.current = setTimeout(() => {
            previewRetryTimerRef.current = 0
            draw()
          }, previewDecision.retryInMs)
        }
      }
    }

    // ── world-space entities ──
    const stableAnchor = getStableScreenAnchor(canvas, t)

    // Visible world-space rect for viewport culling below — draw() previously did
    // the full per-entity color/dash/ctx work for the *entire* manifest on every
    // frame no matter how far zoomed in, which is what made panning/zooming/
    // dragging a selection box freeze on large drawings.
    const view = getVisibleWorldBounds(canvas, t)

    const renderScene = (ctx) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      // ── PDF background image ──
      const pdfBg = propsRef.current.pdfBg
      if (pdfBg?.img?.complete && pdfBg.img.naturalWidth > 0) {
        // PDF world: x in [0, worldW], y in [0, worldH] (DXF Y-up, PDF origin at bottom-left)
        const sx = t.offsetX
        const sy = t.offsetY - pdfBg.worldH * t.scale
        const sw = pdfBg.worldW * t.scale
        const sh = pdfBg.worldH * t.scale
        ctx.globalAlpha = pdfBg.opacity ?? 1
        ctx.drawImage(pdfBg.img, sx, sy, sw, sh)
        ctx.globalAlpha = 1
      }

      // Mirrors the stroke state last set on ctx so consecutive same-style entities skip the
      // (costly) state changes; null = unknown. Branches that set stroke state themselves
      // call invalidateStroke().
      const stroke = { color: null, width: null, dashed: null }
      const setStroke = (color, width, dashed) => {
        if (stroke.color !== color) ctx.strokeStyle = stroke.color = color
        if (stroke.width !== width) ctx.lineWidth = stroke.width = width
        if (stroke.dashed !== dashed) {
          ctx.setLineDash(dashed ? [6 / t.scale, 4 / t.scale] : [])
          stroke.dashed = dashed
        }
      }
      const invalidateStroke = () => { stroke.color = stroke.width = stroke.dashed = null }

      if (m) {
        for (const e of m) {
          const eLayer  = normLayer(e.layer)
          const layerKey = layerVisibilityKey(e, av)

          // Skip entities that are hidden (always respect visibility, even in isolation mode)
          if (!isEntityVisible(e, vl, av)) continue
          const isActive = isEntityInSolo(e, sl, av)

          const isSelected = sh?.has(e.handle)
          const isMoving    = isSelected && ia?.mode === 'move'
          const isFencePreview = fenceRef.current.length === 1 && fencePreviewRef.current.has(e.handle)
          const drawSelectedInOverlay = isSelected && eLayer !== 'Flood' && !isMoving && !isFencePreview && vertexEditRef.current?.handle !== e.handle
          let entityBounds = null

          // Skip entities entirely outside the visible viewport, unless they're
          // actively being moved/vertex-edited (their on-screen bounds can lag
          // one frame behind the cached box while being dragged).
          if (!isMoving && vertexEditRef.current?.handle !== e.handle) {
            entityBounds = getEntityBounds(e)
            if (boundsOutsideView(entityBounds, view)) continue
          }

          // Dim non-solo entities in isolate mode, and split-group context dimming
          const inSplitHighlight = scm?.size > 0 && iso && layerKey === iso
          const inShiftSel = fh ? fh.has(e.handle) : !ssl?.size || inShiftSelection(e, ssl)
          const dimmed = !fh && ((!isActive && sl?.size > 0)
            || (!ssl?.size && !!(iso && !inFocusedClass(e, iso, av)))
            || !!(inSplitHighlight && !scm.has(e.handle)))

          const isLayerHighlight = (fh || ssl?.size > 0) && inShiftSel && !isSelected
          const isImportantWheelEntity = isSelected || isMoving || isFencePreview || isLayerHighlight || vertexEditRef.current?.handle === e.handle
          if (isWheelZooming && entityBounds && !shouldRenderEntityInWheelFrame(entityBounds, t, isImportantWheelEntity)) continue
          const dimmedByShiftSel = !!((fh || ssl?.size > 0) && !inShiftSel && (!isSelected || fh))
          if (drawSelectedInOverlay && !dimmed && !dimmedByShiftSel) continue
          const isUntagged = isClassified && (eLayer === '0' || eLayer === 'Untagged' || !(eLayer in cc))

          const color = e.isUserDrawn
            ? (cc[eLayer] || '#2563eb')
            : e.isProtected
              ? (cc[eLayer] || '#9ca3af')
              : isClassified
                ? (cc[eLayer] || '#9ca3af')
                : layerNameColor(eLayer)

          const selColor = isSelected ? (scm?.get(e.handle) ?? '#2563eb') : color
          setStroke(selColor, (isSelected ? 2.5 : 1) / t.scale, isUntagged && !isClassified)

          // ── Flood layer: filled polygon with hole cutouts (evenodd) ──
          if (eLayer === 'Flood' && e.type === 'LWPOLYLINE' && e.closed && e.points?.length >= 3) {
            const pts = e.points
            const origin = pts[0]
            const [sx, sy] = worldToScreenStable(origin[0], origin[1], t, stableAnchor)
            ctx.save()
            ctx.translate(sx, sy)
            ctx.scale(t.scale, -t.scale)
            // Fill path — always close so the fill region is correct
            ctx.beginPath()
            ctx.moveTo(0, 0)
            for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0] - origin[0], pts[i][1] - origin[1])
            ctx.closePath()
            // Hole sub-paths (evenodd cuts them out)
            for (const hole of (e.holes ?? [])) {
              if (hole.length < 3) continue
              ctx.moveTo(hole[0][0] - origin[0], hole[0][1] - origin[1])
              for (let i = 1; i < hole.length; i++) ctx.lineTo(hole[i][0] - origin[0], hole[i][1] - origin[1])
              ctx.closePath()
            }
            ctx.fillStyle = color
            ctx.globalAlpha = dimmed ? 0.12 : dimmedByShiftSel ? 0.07 : 0.35
            ctx.fill('evenodd')
            // Stroke path — for user-drawn floods, don't close the path so the
            // implicit closing segment (which cuts diagonally across the area) is
            // never stroked.  Auto-generated floods trace a proper closed boundary
            // so they always get the closing stroke.
            ctx.globalAlpha = dimmed ? 0.25 : dimmedByShiftSel ? 0.1 : 1.0
            ctx.strokeStyle = isLayerHighlight && !isClassified ? '#2563eb' : isSelected ? (scm?.get(e.handle) ?? '#2563eb') : color
            ctx.lineWidth = (isSelected ? 2.5 : 1.5) / t.scale
            ctx.beginPath()
            ctx.moveTo(0, 0)
            for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0] - origin[0], pts[i][1] - origin[1])
            if (!e.isUserDrawn) ctx.closePath()
            ctx.stroke()
            ctx.restore()
            continue
          }

          // Substitute live vertex positions while dragging
          const drawE = liveVertexEntity(e, vertexEditRef.current)

          if (isFencePreview) {
            invalidateStroke()
            ctx.strokeStyle = fenceSubtractRef.current ? '#a855f7' : '#f97316'
            ctx.lineWidth = 2.5 / t.scale
            ctx.setLineDash([])
            ctx.globalAlpha = 1
            drawEntity(ctx, drawE, t, stableAnchor)
          } else if (isLayerHighlight) {
            invalidateStroke()
            ctx.setLineDash([])
            if (isClassified) {
              // In classified view: highlight in the layer's own qbiq color, no red
              ctx.globalAlpha = 1
              ctx.strokeStyle = color
              ctx.lineWidth = 1.5 / t.scale
              drawEntity(ctx, drawE, t, stableAnchor)
            } else {
              // In other views: blue glow highlight
              ctx.globalAlpha = 0.35
              ctx.strokeStyle = '#93c5fd'
              ctx.lineWidth = 7 / t.scale
              drawEntity(ctx, drawE, t, stableAnchor)
              ctx.globalAlpha = 1
              ctx.strokeStyle = '#2563eb'
              ctx.lineWidth = 1.5 / t.scale
              drawEntity(ctx, drawE, t, stableAnchor)
            }
          } else if (dimmed) {
            invalidateStroke()
            ctx.strokeStyle = '#c0c8d4'
            ctx.globalAlpha = 0.35
            drawEntity(ctx, drawE, t, stableAnchor)
          } else if (dimmedByShiftSel) {
            ctx.globalAlpha = 0.15
            drawEntity(ctx, drawE, t, stableAnchor)
          } else if (isMoving) {
            invalidateStroke()
            ctx.globalAlpha = 0.2; drawEntity(ctx, drawE, t, stableAnchor)
            const dWX = ia.dWX ?? 0, dWY = ia.dWY ?? 0
            ctx.globalAlpha = 1; ctx.strokeStyle = '#2563eb'
            drawEntity(ctx, drawE, t, stableAnchor, dWX, dWY)
          } else {
            ctx.globalAlpha = 1; drawEntity(ctx, drawE, t, stableAnchor)
          }
          ctx.globalAlpha = 1
        }
      }
      ctx.setLineDash([])

      // Draw selected entities as a dedicated overlay pass. For large selections
      // this keeps the base manifest loop simple and avoids selected-style switches
      // while still keeping the blue selection visible during zoom/pan.
      if (m && sh?.size) {
        const finishOverlayTrace = markPerf('selection.overlay', { cached: false })
        const byHandle = manifestByHandle(m)
        let drawn = 0
        let skipped = 0
        const useSelectedOverlayLod = shouldUseSelectedOverlayLod({
          selectedCount: sh.size,
          isInteracting: isWheelZooming || isSelectingBox || isViewportPanning(panRef.current),
          hasSelectedColorMap: !!scm?.size,
          hasSoloLayers: !!sl?.size,
          hasShiftSelectedLayers: !!ssl?.size,
          hasIsolatedLayer: !!iso,
        })
        ctx.globalAlpha = 1
        ctx.setLineDash([])
        ctx.lineWidth = 2.5 / t.scale
        if (!scm?.size) ctx.strokeStyle = '#2563eb'
        const overlayKey = [
          m, sh, vl, av, sl, ssl, iso, scm, fh,
          ia?.mode === 'move', fencePreviewRef.current, vertexEditRef.current?.handle,
        ]
        if (!isSceneCacheValid(selectedOverlayRef.current, overlayKey)) {
          selectedOverlayRef.current = {
            key: overlayKey,
            list: buildSelectedOverlayList(sh, byHandle, (e, h) => {
              const eLayer = normLayer(e.layer)
              const layerKey = layerVisibilityKey(e, av)
              if (eLayer === 'Flood') return false
              if (!isEntityVisible(e, vl, av)) return false
              if (ia?.mode === 'move' || fencePreviewRef.current.has(h) || vertexEditRef.current?.handle === h) return false
              const isActive = isEntityInSolo(e, sl, av)
              const inSplitHighlight = scm?.size > 0 && iso && layerKey === iso
              const inShiftSel = fh ? fh.has(e.handle) : !ssl?.size || inShiftSelection(e, ssl)
              const dimmed = !fh && ((!isActive && sl?.size > 0)
                || (!ssl?.size && !!(iso && !inFocusedClass(e, iso, av)))
                || !!(inSplitHighlight && !scm.has(h)))
              const dimmedByShiftSel = !!((fh || ssl?.size > 0) && !inShiftSel)
              return !dimmed && !dimmedByShiftSel
            }, getEntityBounds),
          }
        }
        for (const { handle: h, entity: e, bounds: b } of selectedOverlayRef.current.list) {
          if (boundsOutsideView(b, view)) continue
          if (shouldSkipSelectedOverlayEntity({
            useLod: useSelectedOverlayLod,
            screenSizePx: entityScreenSize(b, t),
          })) {
            skipped += 1
            continue
          }
          if (scm?.size) ctx.strokeStyle = scm.get(h) ?? '#2563eb'
          drawEntity(ctx, e, t, stableAnchor)
          drawn += 1
        }
        ctx.globalAlpha = 1
        finishOverlayTrace({
          count: drawn,
          skipped,
          lod: useSelectedOverlayLod,
          thresholdPx: useSelectedOverlayLod ? SELECTED_OVERLAY_LOD_MIN_SCREEN_PX : 0,
        })
      }

      // ── vertex handles (selected LWPOLYLINEs + selected LINEs + flood tool) ──
      // Each handle is 4 ctx calls (beginPath/arc/fill/stroke). Viewport culling
      // (below) keeps this cheap for normal zoomed-in editing; MAX_VERTEX_HANDLES_PER_FRAME
      // is the backstop for when the whole drawing is visible at once (e.g. "select
      // all" while zoomed to fit), where culling can't reduce the count — draw()
      // just stops adding handles past that budget instead of drawing all of them.
      if (!isWheelZooming && !isSelectingBox && m) {
        const ve = vertexEditRef.current
        const byHandle = manifestByHandle(m)
        let hasSelectedEditable = false
        if (!scm?.size && sh?.size) {
          for (const h of sh) {
            const e = byHandle.get(h)
            if (e && (e.type === 'LWPOLYLINE' || e.type === 'LINE')) { hasSelectedEditable = true; break }
          }
        }
        if (hasSelectedEditable || tool === 'flood') {
          ctx.save()
          ctx.translate(t.offsetX, t.offsetY); ctx.scale(t.scale, -t.scale)
          let handlesDrawn = 0
          outer: for (const e of m) {
            const isFloodActive = tool === 'flood' && e.layer === 'Flood' && e.type === 'LWPOLYLINE'
            const isEntitySelected = sh?.has(e.handle)
            if (!isEntitySelected && !isFloodActive) continue
            if (!isEntitySelectable(e, vl, sl, av)) continue
            if (ve?.handle !== e.handle) {
              const b = getEntityBounds(e)
              // Same viewport cull as the main render pass — no point drawing handles
              // for a selected entity that's currently off-screen.
              if (boundsOutsideView(b, view)) continue
              // Too small on screen for individual vertices to be distinguishable/
              // grabbable at all — skip its handles rather than draw an unusable
              // cluster (this is what the "many tiny objects at low zoom" screenshot
              // was showing). The entity itself still renders normally/highlighted.
              if (entityScreenSize(b, t) < MIN_ENTITY_SCREEN_SIZE_FOR_HANDLES_PX) continue
            }
            const handleColor = cc[e.layer] || '#2563eb'

            // LINEs share the polyline grips; a LINE mid-drag may already be a 3-point polyline.
            const shape = editableShape(liveVertexEntity(e, ve))
            if (shape) {
              const { points: pts, bulge, closed } = shape
              const gh = gripHoverRef.current
              // Vertex grips — blue squares (cold), red squares (hot/dragging)
              // Handles render at a fixed screen size regardless of zoom — when
              // zoomed far out, consecutive vertices can be a couple of screen
              // pixels apart and their handles just overlap into a solid blob.
              // Skip a vertex whose handle would visually collide with the last drawn.
              let lastSX = null, lastSY = null
              for (let vi = 0; vi < pts.length; vi++) {
                if (handlesDrawn >= MAX_VERTEX_HANDLES_PER_FRAME) break outer
                const [vx, vy] = pts[vi]
                const isDragging = ve?.mode === 'vertex' && ve?.handle === e.handle && ve.vertexIdx === vi
                const isHot = isDragging || (gh?.handle === e.handle && gh?.mode === 'vertex' && gh?.idx === vi)
                const [sx, sy] = worldToScreen(vx, vy, t)
                if (!isDragging && lastSX !== null) {
                  const dx = sx - lastSX, dy = sy - lastSY
                  if (dx * dx + dy * dy < MIN_VERTEX_HANDLE_SPACING_PX * MIN_VERTEX_HANDLE_SPACING_PX) continue
                }
                lastSX = sx; lastSY = sy
                const r = (isHot ? 7 : 5) / t.scale
                ctx.fillStyle = isHot ? '#ef4444' : '#2563eb'
                ctx.strokeStyle = '#ffffff'
                ctx.lineWidth = 1.5 / t.scale
                ctx.beginPath()
                ctx.rect(vx - r, vy - r, r * 2, r * 2)
                ctx.fill(); ctx.stroke()
                handlesDrawn++
              }
              // Midpoint grips — diamonds between consecutive vertices
              // Straight segments: blue diamond (clicking adds a vertex there)
              // Arc segments: teal diamond at arc midpoint (dragging adjusts curvature)
              for (let si = 0; si < segmentCount(pts, closed) && handlesDrawn < MAX_VERTEX_HANDLES_PER_FRAME; si++) {
                const b = bulge[si] || 0
                const [mx, my] = segMidpoint(...segmentEnds(pts, si), b)
                const r = 5 / t.scale
                if (Math.abs(b) > 0.001) {
                  // Arc midpoint diamond (teal) — drag to adjust curvature
                  const isArcHot = gh?.handle === e.handle && gh?.mode === 'arc' && gh?.idx === si
                  const isArcDrag = ve?.mode === 'arc' && ve?.handle === e.handle && ve.segIdx === si
                  ctx.fillStyle = (isArcHot || isArcDrag) ? '#ef4444' : '#0d9488'
                } else {
                  // Straight midpoint diamond (blue) — click/drag to add vertex
                  const isMidHot = gh?.handle === e.handle && gh?.mode === 'midpoint' && gh?.idx === si
                  const isMidDrag = ve?.mode === 'midpoint' && ve?.handle === e.handle && ve.segIdx === si
                  ctx.fillStyle = (isMidHot || isMidDrag) ? '#ef4444' : '#2563eb'
                }
                ctx.strokeStyle = '#ffffff'
                ctx.lineWidth = 1.5 / t.scale
                ctx.beginPath()
                ctx.moveTo(mx, my - r); ctx.lineTo(mx + r, my)
                ctx.lineTo(mx, my + r); ctx.lineTo(mx - r, my); ctx.closePath()
                ctx.fill(); ctx.stroke()
                handlesDrawn++
              }
              // Add-vertex hint: small diamond marker on the segment under cursor (flood tool)
              if (isFloodActive && floodHoverRef.current) {
                const { x: hx, y: hy } = floodHoverRef.current
                const r = 5 / t.scale
                ctx.beginPath()
                ctx.moveTo(hx, hy - r); ctx.lineTo(hx + r, hy)
                ctx.lineTo(hx, hy + r); ctx.lineTo(hx - r, hy)
                ctx.closePath()
                ctx.fillStyle = 'rgba(255,255,255,0.9)'
                ctx.strokeStyle = handleColor
                ctx.lineWidth = 1.5 / t.scale
                ctx.fill(); ctx.stroke()
                // "+" cross inside diamond
                ctx.beginPath()
                ctx.moveTo(hx - r*0.45, hy); ctx.lineTo(hx + r*0.45, hy)
                ctx.moveTo(hx, hy - r*0.45); ctx.lineTo(hx, hy + r*0.45)
                ctx.strokeStyle = handleColor; ctx.lineWidth = 1.2 / t.scale
                ctx.stroke()
              }
            }
          }
          ctx.restore()
        }
      }
    }

    // While a point-pick tool (Move/Copy/Rotate) is active, or the view is at rest (no zoom/pan/
    // drag/vertex edit), moving the cursor never changes the scene itself — render it into an
    // offscreen bitmap once and make each cursor move a drawImage. The selection rubber band is
    // drawn on top of the scene, and its highlighted set is part of the cache key.
    const pickMode = !!propsRef.current.pickPointMode
    const sceneIa = ia?.mode === 'box' ? null : ia
    const atRest = !isWheelZooming && !sceneIa && !vertexEditRef.current && !isViewportPanning(panRef.current)
    let sceneCached = false
    if (!pickMode) ghostCacheRef.current = null
    if (pickMode || atRest) {
      const key = [
        canvas.width, canvas.height, t.scale, t.offsetX, t.offsetY,
        m, vl, av, cc, sh, scm, iso, tool, sl, ssl, fh,
        propsRef.current.pdfBg, !!propsRef.current.pdfBg?.img?.complete,
        isWheelZooming, isSelectingBox, isViewportPanning(panRef.current), sceneIa,
        vertexEditRef.current, gripHoverRef.current?.handle, gripHoverRef.current?.mode, gripHoverRef.current?.idx, floodHoverRef.current,
        fenceRef.current.length, fencePreviewRef.current, fenceSubtractRef.current,
      ]
      sceneCached = isSceneCacheValid(sceneCacheRef.current, key)
      if (!sceneCached) {
        const buf = sceneCacheRef.current?.canvas ?? document.createElement('canvas')
        if (buf.width !== canvas.width) buf.width = canvas.width
        if (buf.height !== canvas.height) buf.height = canvas.height
        renderScene(buf.getContext('2d'))
        sceneCacheRef.current = { key, canvas: buf }
      }
      ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.setLineDash([])
      ctx.drawImage(sceneCacheRef.current.canvas, 0, 0)
    } else {
      sceneCacheRef.current = null
      renderScene(ctx)
    }

    const room = propsRef.current.roomPreview
    if (room?.length) {
      ctx.save(); ctx.translate(t.offsetX, t.offsetY); ctx.scale(t.scale, -t.scale)
      ctx.beginPath()
      for (const ring of room) {
        ring.forEach(([x, y], i) => i ? ctx.lineTo(x, y) : ctx.moveTo(x, y))
        ctx.closePath()
      }
      ctx.fillStyle = 'rgba(37,99,235,0.22)'; ctx.strokeStyle = '#2563eb'; ctx.lineWidth = 3 / t.scale
      ctx.fill('evenodd'); ctx.stroke()
      ctx.beginPath(); ctx.strokeStyle = '#f97316'; ctx.lineWidth = 4 / t.scale
      ctx.setLineDash([8 / t.scale, 5 / t.scale])
      for (const line of propsRef.current.inferredCorePreview || []) {
        line.forEach(([x,y],i) => i ? ctx.lineTo(x,y) : ctx.moveTo(x,y))
      }
      ctx.stroke(); ctx.restore()
    }

    // ── selection-box overlay ──
    const fp = fenceRef.current
    if (fp.length > 0 && cursorRef.current) {
      const [x1,y1]=fp[0], [x2,y2]=cursorRef.current
      ctx.save(); ctx.translate(t.offsetX, t.offsetY); ctx.scale(t.scale, -t.scale)
      const fSub = fenceSubtractRef.current
      // Right-drag = window (solid blue), left-drag = crossing (dashed green)
      const isWindow = x2 >= x1
      let stroke, fill
      if (fSub) { stroke='#a855f7'; fill='rgba(168,85,247,0.07)' }
      else if (isWindow) { stroke='#2563eb'; fill='rgba(37,99,235,0.07)' }
      else { stroke='#16a34a'; fill='rgba(22,163,74,0.07)' }
      ctx.fillStyle=fill; ctx.strokeStyle=stroke
      ctx.lineWidth=1.5/t.scale
      if (isWindow && !fSub) { ctx.setLineDash([]) } else { ctx.setLineDash([5/t.scale,3/t.scale]) }
      ctx.beginPath(); ctx.rect(x1, y1, x2-x1, y2-y1)
      ctx.fill(); ctx.stroke()
      ctx.setLineDash([]); ctx.fillStyle=stroke
      ctx.beginPath(); ctx.arc(x1,y1,4/t.scale,0,Math.PI*2); ctx.fill()
      ctx.restore()
    }

    // ── polyline draw overlay ──
    const pp = polyRef.current
    if (pp.length > 0) {
      ctx.save(); ctx.translate(t.offsetX, t.offsetY); ctx.scale(t.scale, -t.scale)
      ctx.strokeStyle = cc[propsRef.current.activeLayer] || '#16a34a'
      ctx.lineWidth = 2/t.scale
      ctx.beginPath(); ctx.moveTo(pp[0][0],pp[0][1])
      for (const p of pp.slice(1)) ctx.lineTo(p[0],p[1])
      if (cursorRef.current) ctx.lineTo(cursorRef.current[0],cursorRef.current[1])
      ctx.stroke()
      ctx.fillStyle = ctx.strokeStyle
      for (const p of pp){ctx.beginPath();ctx.arc(p[0],p[1],4/t.scale,0,Math.PI*2);ctx.fill()}
      ctx.restore()
    }

    // ── shape draw overlay ──
    const sp = shapeRef.current
    if (tool === 'shape' && (sp.length > 0 || cursorRef.current)) {
      const shape = propsRef.current.activeShape
      const cur = cursorRef.current
      ctx.save(); ctx.translate(t.offsetX, t.offsetY); ctx.scale(t.scale, -t.scale)
      ctx.strokeStyle = '#2563eb'; ctx.lineWidth = 2/t.scale; ctx.setLineDash([6/t.scale,3/t.scale])
      if (shape === 'rectangle' && sp.length >= 1 && cur) {
        const [x0,y0]=sp[0],[x1,y1]=cur
        ctx.beginPath(); ctx.moveTo(x0,y0); ctx.lineTo(x1,y0); ctx.lineTo(x1,y1); ctx.lineTo(x0,y1); ctx.closePath(); ctx.stroke()
      } else if (shape === 'circle' && sp.length >= 1 && cur) {
        const pts = circlePreviewPts(sp[0], cur)
        ctx.beginPath(); ctx.moveTo(pts[0][0],pts[0][1])
        for (let i=1;i<pts.length;i++) ctx.lineTo(pts[i][0],pts[i][1])
        ctx.closePath(); ctx.stroke()
      } else if (shape === 'arc') {
        if (sp.length >= 1 && cur) {
          if (sp.length === 1) {
            ctx.beginPath(); ctx.moveTo(sp[0][0],sp[0][1]); ctx.lineTo(cur[0],cur[1]); ctx.stroke()
          } else {
            const arcPts = buildArc(sp[0], cur, sp[1], '').points
            ctx.beginPath(); ctx.moveTo(arcPts[0][0],arcPts[0][1])
            for (let i=1;i<arcPts.length;i++) ctx.lineTo(arcPts[i][0],arcPts[i][1])
            ctx.stroke()
          }
        }
      }
      ctx.setLineDash([])
      // Draw placed points as dots
      for (const p of sp) { ctx.beginPath(); ctx.arc(p[0],p[1],4/t.scale,0,Math.PI*2); ctx.fillStyle='#2563eb'; ctx.fill() }
      ctx.restore()
    }

    // ── box rubber-band (screen space) ──
    if ((tool==='select'||tool==='pan') && ia?.mode==='box' && ia.endSX!=null) {
      const x=Math.min(ia.startSX,ia.endSX), y=Math.min(ia.startSY,ia.endSY)
      const w=Math.abs(ia.endSX-ia.startSX), h=Math.abs(ia.endSY-ia.startSY)
      // Right-drag = window (solid blue), left-drag = crossing (dashed green)
      const isWindow = ia.endSX >= ia.startSX
      const stroke = isWindow ? '#2563eb' : '#16a34a'
      const fill = isWindow ? 'rgba(37,99,235,0.06)' : 'rgba(22,163,74,0.06)'
      ctx.save(); ctx.strokeStyle=stroke; ctx.fillStyle=fill; ctx.lineWidth=1
      if (isWindow) { ctx.setLineDash([]) } else { ctx.setLineDash([4,3]) }
      ctx.fillRect(x,y,w,h); ctx.strokeRect(x,y,w,h); ctx.restore()
    }

    // ── scale measure line (screen space) ──
    const { isScaledToCm } = propsRef.current
    if (tool==='scale' && scaleRef.current && cursorRef.current) {
      const p1=scaleRef.current
      const [sx1, sy1] = worldToScreenStable(p1[0], p1[1], t, stableAnchor)
      const [sx2, sy2] = worldToScreenStable(cursorRef.current[0], cursorRef.current[1], t, stableAnchor)
      ctx.save(); ctx.strokeStyle='#f97316'; ctx.lineWidth=2; ctx.setLineDash([6,3])
      ctx.beginPath(); ctx.moveTo(sx1,sy1); ctx.lineTo(sx2,sy2); ctx.stroke()
      ctx.setLineDash([])
      ctx.fillStyle='#f97316'
      ctx.beginPath(); ctx.arc(sx1,sy1,5,0,Math.PI*2); ctx.fill()
      ctx.beginPath(); ctx.arc(sx2,sy2,5,0,Math.PI*2); ctx.fill()
      const dist=Math.hypot(cursorRef.current[0]-p1[0],cursorRef.current[1]-p1[1])
      const unitLabel = isScaledToCm ? 'cm' : 'units'
      ctx.fillStyle='#000000'; ctx.strokeStyle='#f97316'; ctx.lineWidth=1
      ctx.font='bold 11px Inter,sans-serif'; ctx.textAlign='center'; ctx.textBaseline='bottom'
      const mx=(sx1+sx2)/2, my=Math.min(sy1,sy2)-8
      ctx.fillText(`${dist.toFixed(1)} ${unitLabel}`,mx,my)
      ctx.restore()
    }

    // ── move/copy live preview ghost ──
    const preview = propsRef.current.movePreview
    if (preview && cursorRef.current) {
      const [curWX, curWY] = cursorRef.current
      const [baseWX, baseWY] = preview.base
      const dx = curWX - baseWX, dy = curWY - baseWY
      const ghost = getMoveGhost(preview.entities, canvas, t, stableAnchor)
      if (ghost) {
        const [ox, oy] = ghostScreenOffset(dx, dy, t.scale)
        ctx.drawImage(ghost.canvas, ghost.rect.x + ox, ghost.rect.y + oy)
      } else {
        ctx.save()
        ctx.strokeStyle = 'rgba(37,99,235,0.65)'; ctx.lineWidth = 1.5 / t.scale
        ctx.setLineDash([4 / t.scale, 3 / t.scale])
        for (const e of preview.entities) drawEntity(ctx, e, t, stableAnchor, dx, dy)
        ctx.setLineDash([])
        ctx.restore()
      }
    }

    // ── rotate/mirror live preview ghost ──
    const tPrev = propsRef.current.transformPreview
    if (tPrev && cursorRef.current) {
      const [mx, my] = cursorRef.current
      const shift = shiftRef.current

      if (tPrev.mode === 'rotate') {
        const { entities, cx, cy } = tPrev
        let angle = Math.atan2(my - cy, mx - cx)
        if (shift) angle = Math.round(angle / (Math.PI / 4)) * (Math.PI / 4)
        // ghost entities
        ctx.save()
        ctx.strokeStyle = 'rgba(37,99,235,0.65)'; ctx.lineWidth = 1.5 / t.scale
        ctx.setLineDash([4 / t.scale, 3 / t.scale])
        for (const e of entities) drawEntity(ctx, rotateEntity(e, cx, cy, angle), t, stableAnchor)
        ctx.setLineDash([])
        // center crosshair
        const [ccx, ccy] = worldToScreenStable(cx, cy, t, stableAnchor)
        const [msx, msy] = worldToScreenStable(mx, my, t, stableAnchor)
        ctx.strokeStyle = '#2563eb'; ctx.lineWidth = 1.2
        ctx.beginPath(); ctx.moveTo(ccx - 6, ccy); ctx.lineTo(ccx + 6, ccy); ctx.stroke()
        ctx.beginPath(); ctx.moveTo(ccx, ccy - 6); ctx.lineTo(ccx, ccy + 6); ctx.stroke()
        ctx.beginPath(); ctx.arc(ccx, ccy, 2.5, 0, Math.PI * 2); ctx.stroke()
        // angle reference line
        ctx.strokeStyle = 'rgba(37,99,235,0.3)'; ctx.setLineDash([3, 3])
        ctx.beginPath(); ctx.moveTo(ccx, ccy); ctx.lineTo(msx, msy); ctx.stroke()
        ctx.restore()
        // angle label (screen space)
        const [csx, csy] = worldToScreenStable(mx, my, t, stableAnchor)
        const deg = ((angle * 180 / Math.PI) % 360 + 360) % 360
        ctx.save()
        ctx.font = 'bold 11px Inter,sans-serif'; ctx.textAlign = 'left'; ctx.textBaseline = 'middle'
        ctx.strokeStyle = '#fff'; ctx.lineWidth = 3
        ctx.strokeText(`${deg.toFixed(1)}°`, csx + 14, csy)
        ctx.fillStyle = '#2563eb'
        ctx.fillText(`${deg.toFixed(1)}°`, csx + 14, csy)
        ctx.restore()

      }
    }

    // ── pick-point crosshair ──
    if (propsRef.current.pickPointMode && cursorRef.current) {
      const [cwx, cwy] = cursorRef.current
      const [csx, csy] = worldToScreenStable(cwx, cwy, t, stableAnchor)
      ctx.save()
      ctx.strokeStyle = '#2563eb'; ctx.lineWidth = 1.5; ctx.setLineDash([4, 3])
      ctx.beginPath(); ctx.moveTo(csx - 14, csy); ctx.lineTo(csx + 14, csy); ctx.stroke()
      ctx.beginPath(); ctx.moveTo(csx, csy - 14); ctx.lineTo(csx, csy + 14); ctx.stroke()
      ctx.setLineDash([]); ctx.lineWidth = 1.5
      ctx.beginPath(); ctx.arc(csx, csy, 5, 0, Math.PI * 2); ctx.stroke()
      ctx.restore()
    }

    // ── snap indicator ──
    if ((tool === 'draw' || tool === 'scale' || tool === 'pan' || tool === 'shape' || propsRef.current.pickPointMode) && snapRef.current) {
      const { point: sp, type: st } = snapRef.current
      const [ssx, ssy] = worldToScreenStable(sp[0], sp[1], t, stableAnchor)
      ctx.save()
      ctx.lineWidth = 1.5

      const col = SNAP_COLOR[st] ?? '#16a34a'
      ctx.strokeStyle = col
      ctx.fillStyle   = col

      if (st === 'endpoint' || st === 'close') {
        ctx.globalAlpha = 0.22; ctx.fillRect(ssx-5, ssy-5, 10, 10)
        ctx.globalAlpha = 1;    ctx.strokeRect(ssx-5, ssy-5, 10, 10)
      } else if (st === 'midpoint') {
        ctx.beginPath(); ctx.moveTo(ssx, ssy-6); ctx.lineTo(ssx+6, ssy+5); ctx.lineTo(ssx-6, ssy+5); ctx.closePath()
        ctx.globalAlpha = 0.22; ctx.fill()
        ctx.globalAlpha = 1;    ctx.stroke()
      } else if (st === 'center') {
        ctx.globalAlpha = 0.22; ctx.beginPath(); ctx.arc(ssx, ssy, 5, 0, Math.PI*2); ctx.fill()
        ctx.globalAlpha = 1;    ctx.beginPath(); ctx.arc(ssx, ssy, 5, 0, Math.PI*2); ctx.stroke()
      } else if (st === 'quadrant') {
        // Diamond ◇
        ctx.beginPath(); ctx.moveTo(ssx, ssy-6); ctx.lineTo(ssx+5, ssy); ctx.lineTo(ssx, ssy+6); ctx.lineTo(ssx-5, ssy); ctx.closePath()
        ctx.globalAlpha = 0.22; ctx.fill()
        ctx.globalAlpha = 1;    ctx.stroke()
      } else if (st === 'geometric_center') {
        // Circle with inner dot
        ctx.beginPath(); ctx.arc(ssx, ssy, 6, 0, Math.PI*2); ctx.stroke()
        ctx.beginPath(); ctx.arc(ssx, ssy, 2, 0, Math.PI*2); ctx.fill()
      } else if (st === 'intersection') {
        // X mark
        const s = 5
        ctx.beginPath()
        ctx.moveTo(ssx-s, ssy-s); ctx.lineTo(ssx+s, ssy+s)
        ctx.moveTo(ssx+s, ssy-s); ctx.lineTo(ssx-s, ssy+s)
        ctx.stroke()
        ctx.beginPath(); ctx.arc(ssx, ssy, 3, 0, Math.PI*2); ctx.fill()
      } else if (st === 'perpendicular') {
        // Right-angle mark ⌐
        const s = 6
        ctx.beginPath(); ctx.moveTo(ssx, ssy-s); ctx.lineTo(ssx, ssy); ctx.lineTo(ssx+s, ssy); ctx.stroke()
        // small corner fill square
        ctx.fillRect(ssx, ssy-3, 3, 3)
      } else if (st === 'nearest') {
        // Small X
        const s = 4
        ctx.globalAlpha = 0.7
        ctx.beginPath()
        ctx.moveTo(ssx-s, ssy-s); ctx.lineTo(ssx+s, ssy+s)
        ctx.moveTo(ssx+s, ssy-s); ctx.lineTo(ssx-s, ssy+s)
        ctx.stroke()
      }

      ctx.globalAlpha = 1
      ctx.fillStyle = col
      ctx.font = 'bold 9px Inter,sans-serif'; ctx.textAlign = 'left'; ctx.textBaseline = 'bottom'
      ctx.fillText(SNAP_LABEL[st] ?? st, ssx + 9, ssy - 3)
      ctx.restore()
    }

    // ── symbol stamp ghost ──
    if (tool === 'symbolStamp' && cursorRef.current && propsRef.current.stampSymbol) {
      const sym = propsRef.current.stampSymbol
      const [wx, wy] = cursorRef.current
      const { manifest: mf } = propsRef.current
      const bounds = mf?.length ? (() => {
        let minX=Infinity,maxX=-Infinity,minY=Infinity,maxY=-Infinity
        for (const e of mf) {
          if (e.type==='LINE'&&e.start&&e.end){
            minX=Math.min(minX,e.start[0],e.end[0]);maxX=Math.max(maxX,e.start[0],e.end[0])
            minY=Math.min(minY,e.start[1],e.end[1]);maxY=Math.max(maxY,e.start[1],e.end[1])
          } else if (e.type==='LWPOLYLINE'&&e.points?.length){
            for(const[px,py] of e.points){minX=Math.min(minX,px);maxX=Math.max(maxX,px);minY=Math.min(minY,py);maxY=Math.max(maxY,py)}
          }
        }
        return {w:Math.max(maxX-minX,100),h:Math.max(maxY-minY,100)}
      })() : {w:100,h:100}
      const scale = Math.max(bounds.w, bounds.h) / 25 / 100
      ctx.save()
      ctx.translate(t.offsetX, t.offsetY); ctx.scale(t.scale, -t.scale)
      ctx.strokeStyle = cc['Interior Design'] || '#7c3aed'
      ctx.lineWidth = 1.5 / t.scale
      ctx.globalAlpha = 0.6
      for (const e of sym.entities) {
        if (e.type === 'LWPOLYLINE' && e.points?.length >= 2) {
          ctx.beginPath()
          ctx.moveTo(wx + e.points[0][0]*scale, wy + e.points[0][1]*scale)
          for (let i=1;i<e.points.length;i++) ctx.lineTo(wx + e.points[i][0]*scale, wy + e.points[i][1]*scale)
          if (e.closed) ctx.closePath()
          ctx.stroke()
        } else if (e.type === 'LINE' && e.start && e.end) {
          ctx.beginPath()
          ctx.moveTo(wx + e.start[0]*scale, wy + e.start[1]*scale)
          ctx.lineTo(wx + e.end[0]*scale, wy + e.end[1]*scale)
          ctx.stroke()
        } else if (e.type === 'CIRCLE' && e.center) {
          ctx.beginPath()
          ctx.arc(wx + e.center[0]*scale, wy + e.center[1]*scale, e.radius*scale, 0, Math.PI*2)
          ctx.stroke()
        }
      }
      ctx.restore()
    }

    // ── scale bar (drawn last, always on top) ──
    _drawScaleBar(ctx, canvas, t, sf)
    finishDrawTrace({
      entities: m?.length ?? 0,
      selected: sh?.size ?? 0,
      selecting: isSelectingBox,
      wheel: isWheelZooming,
      sceneCached,
    })

  }, [])

  // Coalesce redraws from high-frequency events (wheel, mousemove, resize) into
  // one per animation frame — calling draw() synchronously on every native event
  // let a burst of events (fast scroll, fast mouse) each pay the full O(manifest)
  // redraw cost back-to-back, which is what showed up as freezing on large drawings.
  const requestDraw = useCallback(() => {
    if (rafRef.current) return
    rafRef.current = requestAnimationFrame(() => { rafRef.current = 0; draw() })
  }, [draw])

  useEffect(() => () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    clearTimeout(previewRetryTimerRef.current)
  }, [])

  function _drawScaleBar(ctx, canvas, t, sf) {
    const R = 0
    // Pick a nice bar length
    let barPx, label
    if (sf) {
      const targetCm = (80 / t.scale) * sf
      const mag = Math.pow(10, Math.floor(Math.log10(Math.max(targetCm, 0.1))))
      const n = targetCm / mag
      const niceCm = (n<1.5?1:n<3.5?2:n<7.5?5:10) * mag
      barPx = niceCm / sf * t.scale
      label = niceCm >= 100000 ? `${(niceCm/100000).toFixed(1)} km`
            : niceCm >= 100    ? `${(niceCm/100).toFixed(1)} m`
            :                    `${Math.round(niceCm)} cm`
    } else {
      const raw = 80 / t.scale
      const mag = Math.pow(10, Math.floor(Math.log10(Math.max(raw,0.1))))
      const n = raw/mag; const nice = (n<1.5?1:n<3.5?2:n<7.5?5:10)*mag
      barPx = nice * t.scale
      label = `${nice} u`
    }
    barPx = Math.max(10, Math.min(barPx, canvas.width * 0.3))

    const bx = R + 16, by = canvas.height - 52
    ctx.save()
    ctx.fillStyle = 'rgba(255,255,255,0.88)'
    ctx.beginPath(); ctx.roundRect(bx-8, by-18, barPx+16, 26, 4); ctx.fill()
    ctx.strokeStyle = '#334155'; ctx.lineWidth = 1.5; ctx.lineCap = 'round'
    ctx.beginPath()
    ctx.moveTo(bx, by); ctx.lineTo(bx+barPx, by)
    ctx.moveTo(bx, by-4); ctx.lineTo(bx, by+4)
    ctx.moveTo(bx+barPx, by-4); ctx.lineTo(bx+barPx, by+4)
    ctx.stroke()
    ctx.fillStyle = '#000000'
    ctx.font = 'bold 10px Inter,system-ui,sans-serif'
    ctx.textAlign = 'center'; ctx.textBaseline = 'bottom'
    ctx.fillText(label, bx+barPx/2, by-5)
    ctx.restore()
  }

  // ── fit / zoom ────────────────────────────────────────────────────────────
  const fitToScreen = useCallback(() => {
    const canvas = canvasEl.current
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    if (rect.width > 0) { canvas.width = Math.round(rect.width); canvas.height = Math.round(rect.height) }
    const { manifest: m, pdfBg } = propsRef.current
    const pad = 60
    if (m?.length) {
      const b = computeBounds(m)
      if (!isFinite(b.minX)) return
      const dw = b.maxX-b.minX, dh = b.maxY-b.minY
      if (!dw || !dh) return
      transformRef.current = boundsTransform(canvas, b, pad)
    } else if (pdfBg) {
      const { worldW, worldH } = pdfBg
      const scale = Math.min((canvas.width-pad*2)/worldW,(canvas.height-pad*2)/worldH)*0.95
      const cx = worldW/2, cy = worldH/2
      transformRef.current = { scale, offsetX:canvas.width/2-cx*scale, offsetY:canvas.height/2+cy*scale }
    } else return
    propsRef.current.onZoomChange?.(transformRef.current.scale); draw()
  }, [draw])

  // Zoom so these entities fill the view, with room around them (classify-agent "show me").
  const zoomToHandles = useCallback((handles) => {
    const canvas = canvasEl.current, m = propsRef.current.manifest
    if (!canvas || !m?.length || !handles?.length) return
    const want = new Set(handles)
    const b = computeBounds(m.filter(e => want.has(e.handle)))
    if (!isFinite(b.minX)) return
    const full = computeBounds(m)
    const margin = Math.max(b.maxX-b.minX, b.maxY-b.minY, (full.maxX-full.minX) * 0.05) * 0.4
    transformRef.current = boundsTransform(canvas, { minX: b.minX-margin, maxX: b.maxX+margin, minY: b.minY-margin, maxY: b.maxY+margin }, 30)
    propsRef.current.onZoomChange?.(transformRef.current.scale); draw()
  }, [draw])

  const zoomToRoom = useCallback(rings => {
    const canvas = canvasEl.current
    if (!canvas || !rings?.[0]?.length) return
    const b = computeBounds([{ type: 'LWPOLYLINE', points: rings[0], closed: true }])
    const margin = Math.max(b.maxX - b.minX, b.maxY - b.minY) * 0.25
    transformRef.current = boundsTransform(canvas, { minX: b.minX-margin, maxX: b.maxX+margin, minY: b.minY-margin, maxY: b.maxY+margin }, 30)
    propsRef.current.onZoomChange?.(transformRef.current.scale); draw()
  }, [draw])

  const zoomBy = useCallback((factor) => {
    const canvas = canvasEl.current; if (!canvas) return
    const cx=canvas.width/2, cy=canvas.height/2
    transformRef.current = applyZoomAtScreenPoint(transformRef.current, cx, cy, factor)
    propsRef.current.onZoomChange?.(transformRef.current.scale); draw()
  }, [draw])

  const cancelTool = useCallback(() => {
    fenceRef.current=[]; clearFencePreview(); fenceDragRef.current=null; fenceSubtractRef.current=false; fenceSelectableRef.current=[]; spatialIndexRef.current=null; polyRef.current=[]; cursorRef.current=null; shapeRef.current=[]
    interactRef.current=null; scaleRef.current=null; snapRef.current=null; draw()
  }, [draw])

  useImperativeHandle(ref, () => ({ fitToScreen, zoomBy, cancelTool, zoomToHandles, zoomToRoom }),
    [fitToScreen, zoomBy, cancelTool, zoomToHandles, zoomToRoom])
  useEffect(() => { draw() })

  const hasFitRef = useRef(false)
  useLayoutEffect(() => { hasFitRef.current = false }, [fitKey, pdfBg])
  useLayoutEffect(() => {
    if ((manifest?.length || pdfBg) && !hasFitRef.current) {
      hasFitRef.current = true
      fitToScreen()
    }
  }, [fitKey, manifest, pdfBg, fitToScreen])

  useEffect(() => {
    selectionIndexCacheRef.current = null
    if (!manifest?.length) return undefined
    let cancelled = false
    let idleHandle = null
    let timeoutHandle = null

    const runWarmup = () => {
      if (cancelled) return
      const finishTrace = markPerf('selection.index.build', { source: 'warmup' })
      const selectable = manifest.filter(e => isEntitySelectable(e, visibleLayers, soloLayers, activeView))
      const index = buildSpatialIndex(selectable)
      if (!cancelled) {
        selectionIndexCacheRef.current = {
          manifest,
          visibleLayers,
          soloLayers,
          activeView,
          selectable,
          index,
        }
      }
      finishTrace({ count: selectable.length, committed: !cancelled })
    }

    const scheduleIdle = () => {
      if ('requestIdleCallback' in window) {
        idleHandle = window.requestIdleCallback(runWarmup, { timeout: 1000 })
      } else {
        timeoutHandle = window.setTimeout(runWarmup, 250)
      }
    }

    timeoutHandle = window.setTimeout(scheduleIdle, 120)

    return () => {
      cancelled = true
      if (idleHandle != null && 'cancelIdleCallback' in window) window.cancelIdleCallback(idleHandle)
      if (timeoutHandle != null) window.clearTimeout(timeoutHandle)
    }
  }, [manifest, visibleLayers, soloLayers, activeView])

  // Resizing the bitmap clears it; redraw in the same (pre-paint) callback so
  // the cleared canvas never reaches the screen.
  useResizeObserver(canvasEl, ({ width, height }) => {
    const canvas = canvasEl.current
    const w = Math.round(width), h = Math.round(height)
    if (canvas.width === w && canvas.height === h) return
    canvas.width=w; canvas.height=h
    const { manifest: m, pdfBg: pb } = propsRef.current
    if (!hasFitRef.current && (m?.length || pb)) { hasFitRef.current=true; fitToScreen() }
    else draw()
  })

  const onWheel = useCallback((e) => {
    e.preventDefault()
    const canvas=canvasEl.current
    if (!canvas) return
    wheelActiveRef.current = true
    if (wheelIdleTimerRef.current) clearTimeout(wheelIdleTimerRef.current)
    const rect=canvas.getBoundingClientRect()
    const mx=e.clientX-rect.left, my=e.clientY-rect.top
    transformRef.current = applyZoomAtScreenPoint(transformRef.current, mx, my, getWheelZoomFactor(e))
    propsRef.current.onZoomChange?.(transformRef.current.scale); requestDraw()
    wheelIdleTimerRef.current = setTimeout(() => {
      wheelActiveRef.current = false
      wheelIdleTimerRef.current = 0
      requestDraw()
    }, 120)
  }, [requestDraw])

  useEffect(() => {
    const canvas = canvasEl.current
    if (!canvas) return
    canvas.addEventListener('wheel', onWheel, { passive: false })
    return () => {
      canvas.removeEventListener('wheel', onWheel)
      if (wheelIdleTimerRef.current) clearTimeout(wheelIdleTimerRef.current)
    }
  }, [onWheel])

  function isDoubleClick(e) {
    const now=Date.now(), prev=lastClickRef.current
    const dbl=(now-prev.time<350)&&Math.abs(e.clientX-prev.x)<10&&Math.abs(e.clientY-prev.y)<10
    lastClickRef.current=dbl?{time:0,x:0,y:0}:{time:now,x:e.clientX,y:e.clientY}
    return dbl
  }

  // ── keyboard ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const h = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT') return
      if (e.key === 'Escape') {
        // In draw mode: save current stroke (if any) then exit to pan
        const tool = propsRef.current.activeTool
        if (tool === 'draw') {
          const pts = polyRef.current.slice()
          polyRef.current=[]; cursorRef.current=null; snapRef.current=null; draw()
          if (pts.length >= 2) {
            const layer = propsRef.current.activeLayer || 'Walls'
            propsRef.current.onPolylineComplete?.({ points: pts, layer })
          }
          propsRef.current.onExitDrawTool?.()
        } else {
          cancelTool()
        }
        return
      }
      if (e.key === 'Enter') {
        // Save current stroke, stay in draw tool
        const pts = polyRef.current.slice()
        if (pts.length >= 2) {
          polyRef.current=[]; cursorRef.current=null; draw()
          const layer = propsRef.current.activeLayer || 'Walls'
          propsRef.current.onPolylineComplete?.({ points: pts, layer })
        }
      }
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [cancelTool, draw])

  // Shift toggles fence add/subtract mode, but Shift key events don't fire mousemove —
  // without this, the box preview color can lag behind (or contradict) the mode the
  // completion will actually use if Shift changes while the mouse stays still.
  useEffect(() => {
    const syncFenceSubtract = (e) => {
      if (fenceRef.current.length === 1 && fenceSubtractRef.current !== e.shiftKey) {
        fenceSubtractRef.current = e.shiftKey
        draw()
      }
    }
    window.addEventListener('keydown', syncFenceSubtract)
    window.addEventListener('keyup', syncFenceSubtract)
    return () => {
      window.removeEventListener('keydown', syncFenceSubtract)
      window.removeEventListener('keyup', syncFenceSubtract)
    }
  }, [draw])

  // ── mouse down ────────────────────────────────────────────────────────────
  const onMouseDown = useCallback((e) => {
    if (vertexEditRef.current?.pending) return
    // Middle mouse button (scroll wheel click) = pan
    if (e.button === 1) {
      e.preventDefault()
      const t = transformRef.current
      panRef.current = { x: e.clientX, y: e.clientY, ...t, moved: false, middleBtn: true }
      if (canvasEl.current) canvasEl.current.style.cursor = 'grabbing'
      return
    }
    if (e.button !== 0) return
    const canvas=canvasEl.current, rect=canvas.getBoundingClientRect()
    const sx=e.clientX-rect.left, sy=e.clientY-rect.top
    const t=transformRef.current
    const [wx,wy]=screenToWorld(sx,sy,t)
    const { activeTool:tool, manifest:m, selectedHandles:sh } = propsRef.current

    // A pending selection box must not survive an unrelated interaction taking over —
    // otherwise it silently completes against a stale anchor on some later click.
    const clearPendingFence = () => {
      fenceRef.current = []; clearFencePreview()
      fenceDragRef.current = null; cursorRef.current = null; fenceSelectableRef.current = []; spatialIndexRef.current = null
      fenceSubtractRef.current = false; draw()
    }

    // ── Pick-point mode (Move/Copy/Rotate/Mirror two-click flow) ──
    if (propsRef.current.pickPointMode) {
      if (fenceRef.current.length) clearPendingFence()
      pickCaptureRef.current = { sx, sy, shiftKey: e.shiftKey }
      canvas.style.cursor = 'crosshair'
      return
    }

    // ── Vertex / midpoint / arc-midpoint drag — pan mode: any selected LWPOLYLINE or LINE ──
    //    Vertex grip: drag moves it. Teal (arc) diamond: drag reshapes the arc.
    //    Blue diamond: drag inserts a new vertex and moves it (plain click discards via hasMoved).
    if (tool === 'pan' && m && sh?.size) {
      const byHandle = manifestByHandle(m)
      for (const h of sh) {
        const e = byHandle.get(h), shape = e && editableShape(e)
        if (!shape) continue
        const { points: pts, bulge, closed } = shape
        const grip = hitPolylineGrip(pts, bulge, closed, wx, wy, 14 / t.scale)
        if (!grip) continue
        if (fenceRef.current.length) clearPendingFence()
        const copy = pts.map(p => [...p]), isLine = e.type === 'LINE'
        if (grip.mode === 'vertex') {
          vertexEditRef.current = { mode: 'vertex', handle: e.handle, vertexIdx: grip.idx, points: copy, ...(isLine ? { isLine } : { bulge: [...bulge], closed }) }
        } else if (grip.mode === 'arc') {
          vertexEditRef.current = { mode: 'arc', handle: e.handle, segIdx: grip.idx, points: copy, bulge: bulgeArray(pts, bulge, closed), closed }
        } else {
          const ins = insertVertexAtSegment(copy, bulge, grip.idx, closed)
          vertexEditRef.current = { mode: 'midpoint', handle: e.handle, vertexIdx: grip.idx + 1, points: ins.points, bulge: ins.bulge, closed, hasMoved: false }
        }
        return
      }
    }

    // ── Pan (smart default) ──
    if (tool === 'pan') {
      const { visibleLayers:vl2, soloLayers:sl2, activeView:av2 } = propsRef.current
      const selectionCache = isSelectionIndexCacheValid(selectionIndexCacheRef.current, {
        manifest: m,
        visibleLayers: vl2,
        soloLayers: sl2,
        activeView: av2,
      }) ? selectionIndexCacheRef.current : null
      const selectable = selectionCache?.selectable ?? (m ? m.filter(e => isEntitySelectable(e, vl2, sl2, av2)) : [])
      const tol=8/t.scale, entity=selectable.length?findEntityAtPoint(selectable,wx,wy,tol):null

      // Selection box in progress: second click completes it
      if (fenceRef.current.length === 1) {
        const [ax,ay]=fenceRef.current[0]
        const selectableF = fenceSelectableRef.current
        const src = spatialIndexRef.current ?? selectableF
        fenceRef.current = []; clearFencePreview(); fenceDragRef.current = null; cursorRef.current = null; fenceSelectableRef.current = []; fenceSubtractRef.current = false; spatialIndexRef.current = null
        draw()
        if (m) {
          const mode = wx >= ax ? 'window' : 'crossing'
          const finishTrace = markPerf('selection.complete', { mode, source: 'click' })
          const handles = entitiesInBox(src, ax, ay, wx, wy, mode)
          finishTrace({ count: handles.size })
          propsRef.current.onFenceComplete?.(handles, e.shiftKey)
        }
        return
      }

      // No entity hit: first click anchors selection box
      if (!entity) {
        fenceRef.current = [[wx, wy]]
        clearFencePreview()
        fenceDragRef.current = { sx, sy, moved: false }
        fenceSelectableRef.current = selectable
        if (selectionCache?.index) {
          spatialIndexRef.current = selectionCache.index
        } else {
          const finishTrace = markPerf('selection.index.build', { source: 'sync' })
          spatialIndexRef.current = buildSpatialIndex(selectable)
          finishTrace({ count: selectable.length, committed: true })
        }
        cursorRef.current = [wx, wy]
        draw(); return
      }

      panRef.current={
        x:e.clientX, y:e.clientY, ...t, moved:false, shiftKey:e.shiftKey,
        wx, wy, hitHandle:entity?.handle??null, onSelected:!!(entity&&sh?.has(entity.handle)),
      }
      canvas.style.cursor='grabbing'; return
    }

    // ── Scale measure ──
    if (tool === 'scale') {
      let pt = snapRef.current?.point ?? [wx, wy]
      if (!scaleRef.current) { scaleRef.current=[...pt]; draw() }
      else {
        if (e.shiftKey) pt = orthoConstrain(scaleRef.current, pt[0], pt[1])
        const p1=scaleRef.current; scaleRef.current=null; snapRef.current=null
        propsRef.current.onScaleMeasure?.([p1,[...pt]]); draw()
      }
      return
    }

    // ── Edit floor tool ───────────────────────────────────────────────────────
    // Flood vertices are editable without the entity being selected (the floor itself comes from Detect floor).
    // Priority order:
    //   1. Near a vertex → drag it (move vertex, no new vertex created)
    //   2. Double-click near a segment edge → insert a new vertex at that point
    if (tool === 'flood') {
      if (m) {
        const vtol = 14 / t.scale
        const stol = 12 / t.scale  // segment hit tolerance for add-vertex

        for (const ent of m) {
          if (ent.layer !== 'Flood' || ent.type !== 'LWPOLYLINE' || !ent.points?.length) continue
          const pts = ent.points

          // 1. Near an existing vertex → start drag (moves vertex, never adds one)
          for (let vi = 0; vi < pts.length; vi++) {
            const [vx, vy] = pts[vi]
            if (Math.hypot(wx - vx, wy - vy) <= vtol) {
              vertexEditRef.current = { handle: ent.handle, vertexIdx: vi, points: pts.map(p => [...p]), closed: ent.closed, isFlood: true }
              return
            }
          }

          // 2. Double-click on segment edge → add vertex at cursor position
          if (isDoubleClick(e)) {
            const n = pts.length
            let bestSeg = -1, bestPt = null, bestDist = stol
            for (let i = 0; i < n; i++) {
              const j = ent.closed ? (i + 1) % n : i + 1
              if (j >= n) continue
              const [x1, y1] = pts[i], [x2, y2] = pts[j]
              const dx = x2 - x1, dy = y2 - y1
              const len2 = dx*dx + dy*dy || 1
              // Clamp t away from endpoints so we never land on an existing vertex
              const tc = Math.max(0.05, Math.min(0.95, ((wx-x1)*dx + (wy-y1)*dy) / len2))
              const cx = x1 + tc*dx, cy = y1 + tc*dy
              const d = Math.hypot(wx - cx, wy - cy)
              if (d < bestDist) { bestDist = d; bestSeg = i; bestPt = [cx, cy] }
            }
            if (bestSeg >= 0 && bestPt) {
              const newPts = [...pts.slice(0, bestSeg + 1), bestPt, ...pts.slice(bestSeg + 1)]
              propsRef.current.onPolylineVertexEdit?.(ent.handle, newPts)
              return
            }
          }
        }
      }

      return
    }

    // ── Symbol stamp ──
    if (tool === 'symbolStamp') {
      propsRef.current.onSymbolPlace?.(wx, wy)
      return
    }

    // ── Draw polyline ──
    if (tool === 'draw') {
      let pt = snapRef.current?.point ?? [wx, wy]
      if (e.shiftKey && polyRef.current.length > 0) {
        const anchor = polyRef.current[polyRef.current.length - 1]
        pt = orthoConstrain(anchor, pt[0], pt[1])
      }
      polyRef.current = [...polyRef.current, [...pt]]
      draw(); return
    }

    // ── Shape tool ──
    if (tool === 'shape') {
      const shape = propsRef.current.activeShape
      const pt = [...(snapRef.current?.point ?? [wx, wy])]
      const neededClicks = shape === 'arc' ? 3 : 2
      const next = [...shapeRef.current, pt]
      if (next.length < neededClicks) {
        shapeRef.current = next
        draw()
      } else {
        shapeRef.current = []
        cursorRef.current = null
        // For arc: clicks are [start, end, mid] → reorder for buildArc(start, mid, end)
        const orderedPts = shape === 'arc' ? [next[0], next[2], next[1]] : next
        const entity = buildShapeEntity(shape, orderedPts, propsRef.current.activeLayer || 'Walls')
        if (entity) {
          entity.handle = `drawn-shape-${Date.now()}`
          entity.isUserDrawn = true
          propsRef.current.onShapeComplete?.(entity)
        }
        draw()
      }
      return
    }

  }, [draw])

  // ── mouse move ────────────────────────────────────────────────────────────
  const onMouseMove = useCallback((e) => {
    if (vertexEditRef.current?.pending) return
    shiftRef.current = e.shiftKey
    const canvas=canvasEl.current, rect=canvas.getBoundingClientRect()
    const sx=e.clientX-rect.left, sy=e.clientY-rect.top
    const t=transformRef.current

    // Pan/box-select
    if (panRef.current) {
      snapRef.current = null  // clear snap while dragging
      const dx=e.clientX-panRef.current.x, dy=e.clientY-panRef.current.y
      if (panRef.current.middleBtn) {
        transformRef.current={ scale:panRef.current.scale, offsetX:panRef.current.offsetX+dx, offsetY:panRef.current.offsetY+dy }
        propsRef.current.onZoomChange?.(transformRef.current.scale); requestDraw(); return
      }
      if (!panRef.current.moved&&(Math.abs(dx)>4||Math.abs(dy)>4)){
        panRef.current.moved=true
        if (!panRef.current.onSelected){
          panRef.current.boxMode=true
          panRef.current.boxStartSX=panRef.current.x-rect.left
          panRef.current.boxStartSY=panRef.current.y-rect.top
        }
      }
      if (panRef.current.boxMode){
        panRef.current.endSX=sx; panRef.current.endSY=sy
        interactRef.current={ mode:'box', startSX:panRef.current.boxStartSX, startSY:panRef.current.boxStartSY, endSX:sx, endSY:sy }
        requestDraw(); return
      }
      transformRef.current={ scale:panRef.current.scale, offsetX:panRef.current.offsetX+dx, offsetY:panRef.current.offsetY+dy }
      propsRef.current.onZoomChange?.(transformRef.current.scale); requestDraw(); return
    }

    const { activeTool:tool, manifest:m, snapEnabled:snapOn,
            visibleLayers:vl, soloLayers:sl, activeView:av } = propsRef.current
    const [wx,wy]=screenToWorld(sx,sy,t)

    if (propsRef.current.pickPointMode) {
      const snap = snapOn ? findSnapPoint(m, [], wx, wy, t.scale, vl, sl, av) : null
      snapRef.current = snap
      cursorRef.current = snap ? [...snap.point] : [wx, wy]
      if (canvasEl.current) canvasEl.current.style.cursor = 'crosshair'
      requestDraw()
      return
    }

    if (tool === 'draw') {
      const snap = snapOn ? findSnapPoint(m, polyRef.current, wx, wy, t.scale, vl, sl, av) : null
      snapRef.current = snap
      let [cx, cy] = snap ? snap.point : [wx, wy]
      if (e.shiftKey && polyRef.current.length > 0) {
        const anchor = polyRef.current[polyRef.current.length - 1]
        ;[cx, cy] = orthoConstrain(anchor, cx, cy)
      }
      cursorRef.current = [cx, cy]
      requestDraw()
    } else if (tool === 'scale') {
      const snap = snapOn ? findSnapPoint(m, [], wx, wy, t.scale, vl, sl, av) : null
      snapRef.current = snap
      let [cx, cy] = snap ? snap.point : [wx, wy]
      if (e.shiftKey && scaleRef.current) {
        ;[cx, cy] = orthoConstrain(scaleRef.current, cx, cy)
      }
      cursorRef.current = [cx, cy]
      requestDraw()
    } else if (tool === 'pan') {
      if (vertexEditRef.current) {
        const ve = vertexEditRef.current
        const snap = snapOn ? findSnapPoint(m, [], wx, wy, t.scale, vl, sl, av) : null
        snapRef.current = snap
        let [nx, ny] = snap ? snap.point : [wx, wy]
        if (ve.mode === 'vertex' || ve.mode === 'midpoint') {
          if (e.shiftKey) {
            const { points, vertexIdx, isLine } = ve
            const anchor = isLine
              ? points[1 - vertexIdx]
              : (vertexIdx > 0 ? points[vertexIdx - 1] : points[vertexIdx + 1])
            if (anchor) [nx, ny] = orthoConstrain(anchor, nx, ny)
          }
          ve.points[ve.vertexIdx] = [nx, ny]
          if (ve.mode === 'midpoint') ve.hasMoved = true
        } else if (ve.mode === 'arc') {
          // Drag arc midpoint → update bulge for that segment
          ve.bulge[ve.segIdx] = bulgeFromCursor(...segmentEnds(ve.points, ve.segIdx), [nx, ny])
        }
        requestDraw()
      } else if (fenceRef.current.length === 1) {
        // fencePreviewRef is recomputed inside draw() (at most once per animation
        // frame) instead of here — entitiesInBox does a full segment-intersection
        // scan over every selectable entity, which was too expensive to run on
        // every raw mousemove event while dragging the selection box on a large
        // drawing (the main cause of the freeze while starting/making a selection).
        snapRef.current = null; cursorRef.current = [wx, wy]
        fenceSubtractRef.current = e.shiftKey
        // Held-button drag past a small threshold → treat release as an immediate
        // click-drag-release completion, same as the classic rubber-band marquee.
        if (fenceDragRef.current && (e.buttons & 1) && Math.hypot(sx - fenceDragRef.current.sx, sy - fenceDragRef.current.sy) > 4) {
          fenceDragRef.current.moved = true
        }
        requestDraw()
      } else {
        const snap = snapOn ? findSnapPoint(m, [], wx, wy, t.scale, vl, sl, av) : null
        snapRef.current = snap
        cursorRef.current = snap ? [...snap.point] : [wx, wy]
        // Hover detection for vertex/midpoint/arc grips on selected polylines
        const { selectedHandles:sh2 } = propsRef.current
        let newHover = null
        if (m && sh2?.size && sh2.size <= MAX_SELECTED_GRIP_HOVER_SCAN) {
          const byHandle = manifestByHandle(m)
          for (const h of sh2) {
            const ent = byHandle.get(h), shape = ent && editableShape(ent)
            const grip = shape && hitPolylineGrip(shape.points, shape.bulge, shape.closed, wx, wy, 14 / t.scale)
            if (grip) { newHover = { handle: ent.handle, ...grip }; break }
          }
        }
        gripHoverRef.current = newHover
        requestDraw()
      }
    } else if (tool === 'flood') {
      if (vertexEditRef.current) {
        // Dragging an existing vertex — update its position
        const snap = snapOn ? findSnapPoint(m, [], wx, wy, t.scale, vl, sl, av) : null
        snapRef.current = snap
        let [nx, ny] = snap ? snap.point : [wx, wy]
        if (e.shiftKey) {
          const { points, vertexIdx } = vertexEditRef.current
          const anchor = vertexIdx > 0 ? points[vertexIdx - 1] : points[vertexIdx + 1]
          if (anchor) [nx, ny] = orthoConstrain(anchor, nx, ny)
        }
        vertexEditRef.current.points[vertexEditRef.current.vertexIdx] = [nx, ny]
        floodHoverRef.current = null
        requestDraw()
      } else {
        // Not dragging — track nearest flood segment point for add-vertex indicator
        floodHoverRef.current = null
        const vtol = 14 / t.scale
        const stol = 12 / t.scale
        if (m) {
          outer: for (const ent of m) {
            if (ent.layer !== 'Flood' || ent.type !== 'LWPOLYLINE' || !ent.points?.length) continue
            const pts = ent.points
            // Skip if cursor is near an existing vertex (show handle, not segment hint)
            let nearVertex = false
            for (const [vx, vy] of pts) {
              if (Math.hypot(wx - vx, wy - vy) <= vtol) { nearVertex = true; break }
            }
            if (nearVertex) break outer
            // Find nearest point on any segment
            const n = pts.length
            for (let i = 0; i < n; i++) {
              const j = ent.closed ? (i + 1) % n : i + 1
              if (j >= n) continue
              const [x1, y1] = pts[i], [x2, y2] = pts[j]
              const dx = x2 - x1, dy = y2 - y1
              const len2 = dx*dx + dy*dy || 1
              const tc = Math.max(0.05, Math.min(0.95, ((wx-x1)*dx + (wy-y1)*dy) / len2))
              const cx = x1 + tc*dx, cy = y1 + tc*dy
              if (Math.hypot(wx - cx, wy - cy) <= stol) {
                floodHoverRef.current = { x: cx, y: cy }
                break outer
              }
            }
          }
        }
        cursorRef.current = [wx, wy]
        requestDraw()
      }
    } else if (tool === 'shape') {
      const snap = snapOn ? findSnapPoint(m, [], wx, wy, t.scale, vl, sl, av) : null
      snapRef.current = snap
      cursorRef.current = snap ? [...snap.point] : [wx, wy]
      requestDraw()
    } else if (tool === 'symbolStamp') {
      cursorRef.current = [wx, wy]; requestDraw()
    }
  }, [requestDraw])

  // ── mouse up ──────────────────────────────────────────────────────────────
  const onMouseUp = useCallback((e) => {
    if (vertexEditRef.current?.pending) return
    const t=transformRef.current
    const rect=canvasEl.current?.getBoundingClientRect()
    const sx=e.clientX-(rect?.left??0), sy=e.clientY-(rect?.top??0)

    // Commit polyline vertex / midpoint / arc-midpoint drag
    if (vertexEditRef.current) {
      const edit = vertexEditRef.current
      const { mode, handle, points, bulge, hasMoved } = edit
      snapRef.current = null
      if (mode !== 'midpoint' || hasMoved) {
        edit.pending = true
        const finish = () => requestAnimationFrame(() => {
          if (vertexEditRef.current !== edit) return
          vertexEditRef.current = null
          if (canvasEl.current) draw()
        })
        // Keep the preview until React has committed the validated core and floor.
        Promise.resolve().then(() => propsRef.current.onPolylineVertexEdit?.(handle, points, bulge)).then(finish, finish)
      } else {
        vertexEditRef.current = null
      }
      draw(); return
    }

    // Selection box: releasing after drag completes it (classic rubber-band marquee).
    if (fenceRef.current.length === 1 && fenceDragRef.current?.moved) {
      const [ax, ay] = fenceRef.current[0]
      const [wx, wy] = screenToWorld(sx, sy, t)
      const selectableF = fenceSelectableRef.current
      const src = spatialIndexRef.current ?? selectableF
      fenceRef.current = []; clearFencePreview(); fenceDragRef.current = null; cursorRef.current = null; fenceSelectableRef.current = []; fenceSubtractRef.current = false; spatialIndexRef.current = null
      if (propsRef.current.manifest) {
        const mode = wx >= ax ? 'window' : 'crossing'
        const finishTrace = markPerf('selection.complete', { mode, source: 'drag' })
        const handles = entitiesInBox(src, ax, ay, wx, wy, mode)
        finishTrace({ count: handles.size })
        propsRef.current.onFenceComplete?.(handles, e.shiftKey)
      }
      draw(); return
    }

    if (e.button === 1 && panRef.current?.middleBtn) {
      panRef.current=null
      if (canvasEl.current) canvasEl.current.style.cursor='default'
      draw(); return
    }
    // ── Pick-point mode capture ──
    if (pickCaptureRef.current) {
      const { sx, sy, shiftKey: pickShiftKey } = pickCaptureRef.current
      pickCaptureRef.current = null
      const snap = snapRef.current
      const [pwx, pwy] = snap ? [...snap.point] : screenToWorld(sx, sy, t)
      snapRef.current = null
      if (canvasEl.current) canvasEl.current.style.cursor = 'crosshair'
      propsRef.current.onPointPick?.(pwx, pwy, pickShiftKey ?? false)
      draw()
      return
    }

    if (panRef.current) {
      const pan=panRef.current; panRef.current=null; interactRef.current=null
      if (canvasEl.current) canvasEl.current.style.cursor='default'
      if (!pan.moved) {
        propsRef.current.onClickSelect?.(pan.hitHandle??null, pan.shiftKey)
      } else if (pan.boxMode) {
        const[x1,y1]=screenToWorld(pan.boxStartSX,pan.boxStartSY,t)
        const[x2,y2]=screenToWorld(pan.endSX??pan.boxStartSX,pan.endSY??pan.boxStartSY,t)
        const { visibleLayers:vlb, soloLayers:slb, activeView:avb, manifest:mb } = propsRef.current
        const selectableB = (mb??[]).filter(e => isEntitySelectable(e, vlb, slb, avb))
        const mode = pan.endSX >= pan.boxStartSX ? 'window' : 'crossing'
        const finishTrace = markPerf('selection.complete', { mode, source: 'box' })
        const handles = entitiesInBox(selectableB, x1, y1, x2, y2, mode)
        finishTrace({ count: handles.size, candidates: selectableB.length })
        propsRef.current.onBoxSelectComplete?.(handles, pan.shiftKey)
      }
      draw(); return
    }
  }, [draw])

  const onMouseLeave = useCallback(() => {
    panRef.current=null; cursorRef.current=null; if (!vertexEditRef.current?.pending) vertexEditRef.current=null; floodHoverRef.current=null; draw()
  }, [draw])

  // ── right-click: delete vertex ────────────────────────────────────────────
  // Right-click context menu — flood tool silently deletes vertex; pan tool shows AutoCAD-style menu.
  const onContextMenu = useCallback((e) => {
    e.preventDefault()
    if (vertexEditRef.current?.pending) return
    const canvas=canvasEl.current, rect=canvas.getBoundingClientRect()
    const sx=e.clientX-rect.left, sy=e.clientY-rect.top
    const t=transformRef.current
    const [wx,wy]=screenToWorld(sx,sy,t)
    const { activeTool:tool, manifest:m, selectedHandles:sh } = propsRef.current
    if (!m) return
    const vtol = 10 / t.scale

    // Flood mode: right-click on a vertex silently deletes it (existing behavior)
    if (tool === 'flood') {
      for (const ent of m) {
        if (ent.layer !== 'Flood' || ent.type !== 'LWPOLYLINE' || !ent.points?.length) continue
        for (let vi = 0; vi < ent.points.length; vi++) {
          const [vx, vy] = ent.points[vi]
          if (Math.hypot(wx - vx, wy - vy) > vtol) continue
          const minPts = ent.closed ? 3 : 2
          if (ent.points.length <= minPts) return
          const newPts = ent.points.filter((_, i) => i !== vi)
          propsRef.current.onPolylineVertexEdit?.(ent.handle, newPts)
          return
        }
      }
      return
    }

    // Pan mode: show context menu for any selected LWPOLYLINE or LINE near the click.
    // A LINE is edited as a 2-point, single-segment shape (see editableShape).
    if (tool !== 'pan' || !sh?.size) return
    const byHandle = manifestByHandle(m)
    for (const h of sh) {
      const ent = byHandle.get(h), shape = ent && editableShape(ent)
      if (!shape) continue
      const { points: pts, bulge, closed } = shape
      const grip = hitPolylineGrip(pts, bulge, closed, wx, wy, vtol)
      const hitVertex = grip?.mode === 'vertex' ? grip.idx : null
      const hitMidpoint = grip && grip.mode !== 'vertex' ? grip.idx : null
      // Nearest point on the drawn curve for "Add Vertex" away from a grip
      const nearest = nearestSegmentPoint(pts, bulge, closed, wx, wy, 30 / t.scale)
      if (!grip && !nearest) continue
      setVertexMenu({
        handle: ent.handle, entity: { ...ent, ...shape },
        hitVertex, hitMidpoint, nearest,
        x: e.clientX, y: e.clientY,
      })
      return
    }
  }, [setVertexMenu])

  const onDoubleClick = useCallback(() => {
    if (propsRef.current.activeTool === 'pan') fitToScreen()
  }, [fitToScreen])

  // ── Context menu actions ──────────────────────────────────────────────────
  const handleAddVertex = useCallback(() => {
    if (!vertexMenu) return
    const { handle, entity, hitMidpoint, nearest } = vertexMenu
    setVertexMenu(null)
    const [si, f] = hitMidpoint !== null ? [hitMidpoint, 0.5] : [nearest?.si, nearest?.f]
    if (si == null) return
    const r = insertVertexAtSegment(entity.points, entity.bulge, si, entity.closed, f)
    propsRef.current.onPolylineVertexEdit?.(handle, r.points, r.bulge)
  }, [vertexMenu])

  const handleRemoveVertex = useCallback(() => {
    if (!vertexMenu || vertexMenu.hitVertex === null) return
    const { handle, entity, hitVertex } = vertexMenu
    setVertexMenu(null)
    const r = removeVertex(entity.points, entity.bulge, hitVertex, entity.closed)
    if (r) propsRef.current.onPolylineVertexEdit?.(handle, r.points, r.bulge)
  }, [vertexMenu])

  const handleConvertToArc = useCallback(() => {
    if (!vertexMenu || vertexMenu.hitMidpoint === null) return
    const { handle, entity, hitMidpoint } = vertexMenu
    setVertexMenu(null)
    propsRef.current.onPolylineVertexEdit?.(handle, entity.points, toggleSegmentArc(entity.points, entity.bulge, hitMidpoint, entity.closed))
  }, [vertexMenu])

  // Open polylines can only split at an *interior* vertex — splitting at an
  // endpoint would just hand back one empty and one unchanged piece. Closed
  // polylines have no endpoint, so any vertex is a valid seam to open at. From
  // a midpoint diamond instead, App.jsx inserts a vertex there first, then
  // splits on it — that always produces two valid halves, no endpoint check.
  const handleSplitVertex = useCallback(() => {
    if (!vertexMenu) return
    const { handle, entity, hitVertex: vi, hitMidpoint } = vertexMenu
    setVertexMenu(null)
    if (vi !== null) {
      if (!entity.closed && (vi === 0 || vi === entity.points.length - 1)) return
      propsRef.current.onPolylineSplit?.(handle, vi)
    } else if (hitMidpoint !== null) {
      propsRef.current.onPolylineSplit?.(handle, null, hitMidpoint)
    }
  }, [vertexMenu])

  return (
    <div style={{ position:'relative', width:'100%', height:'100%' }}>
      <canvas
        ref={canvasEl}
        style={{ width:'100%', height:'100%', display:'block', cursor: (['scale','flood','symbolStamp'].includes(propsRef.current.activeTool))?'crosshair':'default' }}

        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
        onContextMenu={onContextMenu}
        onDoubleClick={onDoubleClick}
      />
      {vertexMenu && (() => {
        // Remove only makes sense on an actual polyline vertex (a LINE has no
        // vertex to remove). Split and Convert to Arc both work from a LINE's
        // midpoint diamond too — Split inserts a vertex there first (promoting
        // it into an LWPOLYLINE), same as Convert to Arc bowing that segment.
        const { canRemove, canSplit, canConvert, isArc } = vertexMenuActions(vertexMenu)
        const menuItem = (label, onClick, enabled = true) => (
          <div
            style={{ padding:'6px 14px', cursor: enabled ? 'pointer' : 'default',
              color: enabled ? '#f1f5f9' : '#64748b', fontSize:13, lineHeight:'22px', userSelect:'none' }}
            onMouseEnter={e => { if (enabled) e.currentTarget.style.background='#334155' }}
            onMouseLeave={e => { e.currentTarget.style.background='' }}
            onClick={enabled ? onClick : undefined}
          >{label}</div>
        )
        return (
          <>
            <div style={{ position:'fixed', inset:0, zIndex:9998 }} onMouseDown={() => setVertexMenu(null)} />
            <div
              style={{ position:'fixed', left:vertexMenu.x, top:vertexMenu.y, zIndex:9999,
                background:'#1e293b', border:'1px solid #334155', borderRadius:4,
                boxShadow:'0 4px 12px rgba(0,0,0,0.5)', minWidth:160, overflow:'hidden' }}
            >
              {menuItem('Add Vertex', handleAddVertex)}
              {menuItem('Remove Vertex', handleRemoveVertex, canRemove)}
              {menuItem('Split', handleSplitVertex, canSplit)}
              {menuItem(isArc ? 'Convert to Line' : 'Convert to Arc', handleConvertToArc, canConvert)}
            </div>
          </>
        )
      })()}
    </div>
  )
})

export default DxfCanvas
